import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { PageHeader, StatCard } from '../components/common';
import { adminApi } from '../utils/api';
import {
  People as PeopleIcon,
  Restaurant as RecipeIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';

// Map UI period to API period
const periodMap = {
  '7d': 'week',
  '30d': 'month',
  '90d': 'all', // Use 'all' for 90 days since we don't have a 90-day option
};

const Analytics = () => {
  const theme = useTheme();
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const apiPeriod = periodMap[period] || 'week';
      const [userGrowth, recipeStats, searchAnalytics] = await Promise.all([
        adminApi.getUserGrowth(apiPeriod),
        adminApi.getRecipeStats(apiPeriod),
        adminApi.getSearchAnalytics(apiPeriod),
      ]);
      setData({
        userGrowth: userGrowth.data,
        recipeStats: recipeStats.data,
        searchAnalytics: searchAnalytics.data,
      });
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      // Show empty state on error - no mock data
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 1.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontWeight: 500 }}
            >
              {entry.name}: {entry.value.toLocaleString()}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <PageHeader
        title="analytics"
        subtitle="insights and metrics for your app"
        breadcrumbs={[{ label: 'analytics' }]}
        action={
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(e, v) => v && setPeriod(v)}
            size="small"
          >
            <ToggleButton value="7d">7 Days</ToggleButton>
            <ToggleButton value="30d">30 Days</ToggleButton>
            <ToggleButton value="90d">90 Days</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={data?.userGrowth?.total}
            icon={PeopleIcon}
            trend={data?.userGrowth?.trend}
            trendLabel={`+${data?.userGrowth?.new} this period`}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Recipes"
            value={data?.recipeStats?.total}
            icon={RecipeIcon}
            trend={data?.recipeStats?.trend}
            trendLabel={`+${data?.recipeStats?.imported} imported`}
            color="secondary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Searches"
            value={data?.searchAnalytics?.totalSearches}
            icon={SearchIcon}
            trendLabel={data?.searchAnalytics?.totalClicks ? `${data.searchAnalytics.totalClicks} clicks` : undefined}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="External Searches"
            value={data?.searchAnalytics?.externalSearches}
            icon={TrendingIcon}
            trendLabel={data?.searchAnalytics?.totalSearches > 0 
              ? `${Math.round((data.searchAnalytics.externalSearches / data.searchAnalytics.totalSearches) * 100)}% of total`
              : undefined}
            color="warning"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* User Growth */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                User Growth
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={data?.userGrowth?.timeline || []}>
                    <defs>
                      <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="users"
                      name="Total Users"
                      stroke={theme.palette.primary.main}
                      fillOpacity={1}
                      fill="url(#colorUsers)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recipe Activity */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                Recipe Activity
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data?.recipeStats?.timeline || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="imported" name="Imported" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="created" name="Created" fill={theme.palette.secondary.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recipe Sources */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                Recipe Sources
              </Typography>
              {loading ? (
                <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={data?.recipeStats?.bySource || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {data?.recipeStats?.bySource?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Search Trends */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                Search Trends
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={data?.searchAnalytics?.searchTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="internal" name="Internal Search" stroke={theme.palette.primary.main} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="external" name="External Search" stroke={theme.palette.secondary.main} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Queries */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                Top Search Queries
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              ) : data?.searchAnalytics?.topQueries?.length > 0 ? (
                <Grid container spacing={2}>
                  {data.searchAnalytics.topQueries.map((query, index) => (
                    <Grid item xs={12} sm={6} md={4} key={query.query}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: theme.palette.background.elevated,
                          textAlign: 'center',
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{ color: theme.palette.primary.main, mb: 0.5 }}
                        >
                          #{index + 1}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {query.query}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {query.count} searches
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 4 }}>
                  No search data yet. Searches will appear here once users start searching.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Zero Result Searches - Content Gaps */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Zero Results
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 2 }}>
                Searches that returned no results - potential content gaps
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
              ) : data?.searchAnalytics?.topZeroResults?.length > 0 ? (
                <Box>
                  {data.searchAnalytics.topZeroResults.slice(0, 5).map((query, index) => (
                    <Box
                      key={query.query}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        borderBottom: index < 4 ? `1px solid ${theme.palette.divider}` : 'none',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {query.query}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.error.main }}>
                        {query.count}x
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 2 }}>
                  No zero-result searches yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
